import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const BellContainer = styled.div`
  position: relative;
`;

const BellButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  color: var(--color-gray-700);
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background: #EF4444;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 380px;
  max-height: 500px;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-gray-200);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 1000;
`;

const DropdownHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DropdownTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const MarkAllReadButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 0.875rem;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: var(--color-gray-400);
    cursor: not-allowed;
  }
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
`;

const NotificationItem = styled.div<{ $isRead: boolean }>`
  padding: 1rem;
  border-bottom: 1px solid var(--color-gray-100);
  cursor: pointer;
  background: ${props => props.$isRead ? 'white' : 'var(--color-blue-50)'};
  transition: background 0.2s ease;

  &:hover {
    background: var(--color-gray-50);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: var(--color-gray-900);
  font-size: 0.875rem;
  flex: 1;
`;

const NotificationTime = styled.div`
  font-size: 0.75rem;
  color: var(--color-gray-500);
  white-space: nowrap;
  margin-left: 0.5rem;
`;

const NotificationMessage = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  line-height: 1.4;
`;

const EmptyState = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: var(--color-gray-500);
`;

const ConnectionStatus = styled.div<{ $isConnected: boolean }>`
  position: absolute;
  bottom: 3px;
  right: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$isConnected ? '#10B981' : '#EF4444'};
  border: 2px solid white;
`;

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, isConnected, markAsRead, markAllAsRead } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.link) {
      navigate(notification.link);
    }

    setIsOpen(false);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  return (
    <BellContainer ref={dropdownRef}>
      <BellButton onClick={handleBellClick} aria-label="Notifications">
        🔔
        {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
        <ConnectionStatus $isConnected={isConnected} title={isConnected ? 'Connected' : 'Disconnected'} />
      </BellButton>

      <Dropdown $isOpen={isOpen}>
        <DropdownHeader>
          <DropdownTitle>Notifications</DropdownTitle>
          {unreadCount > 0 && (
            <MarkAllReadButton onClick={handleMarkAllRead}>
              Mark all read
            </MarkAllReadButton>
          )}
        </DropdownHeader>

        <NotificationList>
          {notifications.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔔</div>
              <div>No notifications yet</div>
            </EmptyState>
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                $isRead={notification.isRead}
                onClick={() => handleNotificationClick(notification)}
              >
                <NotificationHeader>
                  <NotificationTitle>{notification.title}</NotificationTitle>
                  <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                </NotificationHeader>
                <NotificationMessage>{notification.message}</NotificationMessage>
              </NotificationItem>
            ))
          )}
        </NotificationList>
      </Dropdown>
    </BellContainer>
  );
};
